<template>
    <main id="view-search-accessories-filters" class="products-filters-view">
        <form class="filters-wrapper" @submit.prevent="search()">
            <h1 class="title-with-icon mobile-top-icon">
                <span class="icon-search" :class="brandColor"></span>
                {{ $t("select_accessories") }}
            </h1>

            <div class="select-main-filters">
                <div class="tabs" v-if="!isPhone">
                    <span
                        v-for="tab in tabs"
                        :key="tab.code"
                        class="tab"
                        :class="{ active: selectedTab === tab.code }"
                        @click="selectTab(tab.code)"
                    >
                        {{ tab.label }}
                    </span>
                </div>
                <b-select
                    v-else
                    v-model="selectedTab"
                    class="select-tab"
                    size="is-large"
                    rounded
                    expanded
                    @input="selectTab($event)"
                >
                    <option
                        v-for="tab in tabs"
                        :key="tab.code"
                        :value="tab.code"
                    >
                        {{ tab.label }}
                    </option>
                </b-select>
            </div>

            <div class="categories-list-container" v-if="selectedTab === 'categories'">
                <div class="categories-list">
                    <div
                        v-for="category in categories.children"
                        :key="category.code"
                        class="category-item"
                        :class="{'current': category.code === selectedCategory}"
                        :data-tracking="`accessory_search__button__${category.code}`"
                        @click="selectCategory(category.code)"
                    >
                        <img
                            :src="categoryImg(category)"
                            alt=""
                            width="462"
                            height="300"
                            class="picture"
                        >
                        <div class="title">{{ category.label }}</div>
                        <span class="radio-container">
                            <b-radio v-model="selectedCategory" :native-value="category.code"/>
                        </span>
                    </div>
                </div>
            </div>

            <div class="search-by-application" v-if="selectedTab === 'applications'">
                <b-select v-model="selectedApplication" size="is-large" rounded>
                    <option
                        v-for="application in applications"
                        :value="application.code"
                        :key="application.code"
                    >{{ application.label }}</option>
                </b-select>
            </div>

            <h2 class="filter-step-title">{{ $t("by_machine") }}</h2>

            <div class="search-by-machine" id="search-by-machine">
                <div class="columns is-vcentered">
                    <div class="column is-narrow">
                        {{ $t("compatible_with") }}
                    </div>
                    <div class="column">
                        <autocomplete-product
                            category="machines"
                            :namePreset="selectedMachineNamePreset"
                            @updateAutocompleteTerms="autocompleteTermsUpdated($event)"
                            @productSelected="selectMachine($event)"
                            :allow-old="1"
                        />
                    </div>
                </div>
            </div>

            <button
                type="submit"
                class="button is-primary is-large is-rounded is-uppercase with-small-side-padding submit-search"
                :class="brandBackgroundColor"
            >
                <span>{{ $t("validate") }}</span>
                <span class="icon-arrow-right is-tiny is-right-side"></span>
            </button>
        </form>
    </main>
</template>


<script>
import {mapGetters, mapState} from 'vuex'

import formMixins from "@/common/mixins/form";
import AutocompleteProduct from "@/components/Autocomplete/AutocompleteProduct.vue"
import productService from "@/services/serli/product";

export default {
    name: 'searchAccessoriesFilters',
    mixins: [
        formMixins
    ],
    components:{
        AutocompleteProduct
    },
    data() {
        return {
            tabs: [
                {
                    code: "categories",
                    label: this.$t('by_category')
                },
                {
                    code: "applications",
                    label: this.$t('by_application')
                }
            ],
            selectedTab: 'categories',
            selectedCategory: null,
            selectedApplication: null,
            selectedMachine: null,
            selectedMachineNamePreset: null,
            autocompleteTermsFeedback: '',
            start: document.body.scrollTop,
            change: 0 - 1250,
            currentTime: 0,
            increment: 20
        };
    },
    computed: {
        ...mapState({
            isPhone: state => state.settings.isPhone,
        }),
        ...mapGetters({
          localeKebabFormat: 'settings/localeKebabFormat'
        }),
    },
    asyncComputed: {
       categories() {
          this.startLoading();
           return productService.categories({
            code:'attachments',
            full:true,
            locale: this.localeKebabFormat,
            brand: this.$store.state.settings.brand
          })
           .then(response => response.data.data)
           .finally(() => this.stopLoading());
       },


        applications() {
            this.startLoading();
            return productService.applications({
              locale: this.localeKebabFormat,
            })
            .then(response => {
                let applications = response.data.data;

                applications=[{code: null,label: this.$t('all_xx')}, ...applications];

                applications = applications.sort((a, b) => {
                    let attrToCompareA = (a.label ? a.label: a.code).toLowerCase()
                    let attrToCompareB = (b.label ? b.label: b.code).toLowerCase()
                    if(attrToCompareA < attrToCompareB) { return -1; }
                    if(attrToCompareA > attrToCompareB) { return 1; }
                    return 0;
                })

                return applications;
            })
            .finally(() => this.stopLoading());
        }
    },
    created() {
        if (this.$route.query.init){
            this.$store.commit('searchAccessories/setCategoryFilter', null);
            this.$store.commit('searchAccessories/setApplicationFilter', null);
            this.$store.commit('searchAccessories/setMachineFilter', null);
        }
        else {
            if(this.$store.state.searchAccessories.categoryFilter) {
                this.selectCategory(this.$store.state.searchAccessories.categoryFilter.code);
                if(this.$store.state.searchAccessories.categoryFilter.code)
                    this.selectedTab = 'categories';
            }
            if(this.$store.state.searchAccessories.applicationFilter) {
                this.selectApplication(this.$store.state.searchAccessories.applicationFilter.code);
                if(this.$store.state.searchAccessories.applicationFilter.code)
                    this.selectedTab = 'applications';
            }
            if(this.$store.state.searchAccessories.machineFilter) {
                this.selectMachine(this.$store.state.searchAccessories.machineFilter);
                let name = this.$store.state.searchAccessories.machineFilter.values.reference_name;
                this.selectedMachineNamePreset = name;
                this.autocompleteTermsFeedback = name;
            }
        }
    },
    methods: {
        categoryImg(category) {
            try {
                return require(`@/assets/img/categories-accessories/${category.code}.jpg`)
            } catch (e) {
                return require(`@/assets/img/no_image_available.jpg`);
            }
        },
       selectCategory(code,selectFromList = true) {
           if(this.selectedCategory ===  code)
               this.selectedCategory = null;
           else
               this.selectedCategory = code;

         if(selectFromList) {
           let topScroll = document.getElementById('search-by-machine').offsetTop + 70;
           if(parseInt(window.innerWidth) > 768) topScroll = document.getElementById('search-by-machine').offsetTop;
           setTimeout(() => {
             window.scroll({
               top: topScroll,
               left: 0,
               behavior: 'smooth'
             });
           }, 150);
         }
       },

        selectApplication(application) {
            this.selectedApplication = application;
        },
        selectMachine(product){
            this.selectedMachine = product;
        },
        autocompleteTermsUpdated(terms){
            this.autocompleteTermsFeedback = terms;
        },
        search() {
            this.$store.commit("searchAccessories/setCategoryFilter", (this.selectedCategory ? this.categories.children.find(x => x.code === this.selectedCategory) : null));
            let application = this.applications.find(x => x.code === this.selectedApplication);
            this.$store.commit("searchAccessories/setApplicationFilter", application);
            this.$store.commit("searchAccessories/setMachineFilter", (this.autocompleteTermsFeedback.length > 0 ? this.selectedMachine : null));
            this.$router.push({ path: "search-accessories-results" });
        },
        selectTab(tab) {
            if(this.selectedTab !== tab){
                if(tab === 'categories')
                    this.selectedApplication = null;
                else if(tab === 'applications')
                    this.selectedCategory = null;
                this.selectedTab = tab;
            }
        },
    }
};
</script>

<style lang="scss">
@import "~@/assets/scss/config/config";

#view-search-accessories-filters {
    min-height: 500px;

    .select-main-filters {
        margin: 40px 0 30px 0;
    }

    .search-by-machine {
        margin: 0 auto;
        max-width: 600px;
        margin-top: 20px;
    }

    .submit-search {
        margin-top: 60px;
    }

    .search-by-application {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }
}
</style>
